<template>
  <div>

    <!-- ORIGIN ADDRESS -->
    <div class="columns dp-flex">
      <div class="column">
        <b-field
          id="origin-address"
          :label="$t('kyc.origin_address')"
        >
          <b-input
            v-validate="'required'"
            v-model="form.originAddressLineOne"
            :placeholder="$t('kyc.address_placeholder')"
            name="origin address"
            autocomplete="disabled"
            expanded
            data-test="kyc-address--origin-address-field"
            @blur="$emit('trackInputForm', { key: 'originAddressLineOne', name: 'origin address', page: 'Address' })"
          />
        </b-field>
        <span class="is-invalid">{{ errors.first('origin address') }}</span>
      </div>
    </div>
    <div class="columns dp-flex">
      <div class="column">
        <b-field :label="$t('kyc.address_line_2')">
          <b-input
            v-model="form.originAddressLineTwo"
            :placeholder="$t('kyc.address_placeholder')"
            name="origin address line two"
            autocomplete="disabled"
            expanded
            data-test="kyc-address--origin-address-line-2-field"
            @blur="$emit('trackInputForm', { key: 'originAddressLineTwo', name: 'origin address line two', page: 'Address' })"
          />
        </b-field>
      </div>
    </div>
    <div class="columns dp-flex">
      <div class="column">
        <b-field
          id="origin-country"
          :label="$t('kyc.country')"
          class="kyc-country-is-expanded"
        >
          <b-select
            v-validate="'required'"
            v-model="form.originCountry"
            :placeholder="$t('kyc.country')"
            name="origin country"
            data-test="kyc-address--origin-country-field"
            @blur="$emit('trackInputForm', { key: 'originCountry', name: 'origin country', page: 'Address' })"
            @input="() => {
              form.originProvince = null
              $forceUpdate()
            }"
          >
            <option
              v-for="(country, index) in countries"
              :value="country.en_short_name"
              :key="index"
              :disabled="country.disabled"
            >
              {{ country.en_short_name }}
            </option>
          </b-select>
        </b-field>
        <span class="is-invalid">{{ errors.first('origin country') }}</span>
      </div>
      <div class="column">
        <b-field
          id="origin-postcode"
          :label="$t('kyc.zipcode')"
        >
          <ThailandAutoComplete
            v-validate="form.originCountry === 'Thailand' ? 'required|numeric|length:5': 'required'"
            v-if="form.originCountry === 'Thailand'"
            v-model="form.originPostcode"
            :placeholder="$t('kyc.zipcode')"
            color="#337ab7"
            name="origin postcode"
            type="zipcode"
            data-test="kyc-address--origin-postcode-field"
            autocomplete="disabled"
            expanded
            @select="(address) => selectThailandAddrAutoComplete('origin', address)"
            @blur="$emit('trackInputForm', { key: 'originPostcode', name: 'origin postcode', page: 'Address' })"
          />
          <b-input
            v-validate="form.originCountry === 'Thailand' ? 'required|numeric|length:5': 'required'"
            v-else
            v-model="form.originPostcode"
            :placeholder="$t('kyc.zipcode')"
            name="origin postcode"
            autocomplete="disabled"
            expanded
            data-test="kyc-address--origin-postcode-field"
            @blur="$emit('trackInputForm', { key: 'originPostcode', name: 'origin postcode', page: 'Address' })"
          />
        </b-field>
        <span class="is-invalid">{{ errors.first('origin postcode') }}</span>
      </div>
    </div>
    <div class="columns dp-flex">
      <div class="column">
        <b-field
          id="origin-city"
          :label="$t('kyc.city')"
        >
          <ThailandAutoComplete
            v-validate="'required'"
            v-if="form.originCountry === 'Thailand'"
            v-model="form.originCity"
            :placeholder="$t('kyc.city_placeholder')"
            color="#337ab7"
            name="origin city"
            type="amphoe"
            data-test="kyc-address--origin-city-field"
            expanded
            @select="(address) => selectThailandAddrAutoComplete('origin', address)"
            @blur="$emit('trackInputForm', { key: 'originCity', name: 'origin city', page: 'Address' })"
          />
          <b-input
            v-validate="'required'"
            v-else
            v-model="form.originCity"
            :placeholder="$t('kyc.city_placeholder')"
            name="origin city"
            autocomplete="disabled"
            expanded
            data-test="kyc-address--origin-city-field"
            @blur="$emit('trackInputForm', { key: 'originCity', name: 'origin city', page: 'Address' })"
          />
        </b-field>
        <span class="is-invalid">{{ errors.first('origin city') }}</span>
      </div>
      <div class="column">
        <b-field
          id="origin-province"
          :label="$t('kyc.province')"
        >
          <b-select
            v-validate="'required'"
            v-if="form.originCountry === 'Thailand'"
            v-model="form.originProvince"
            :placeholder="$t('kyc.province_placeholder')"
            name="origin province"
            expanded
            data-test="kyc-address--origin-province-field"
            @blur="$emit('trackInputForm', { key: 'originProvince', name: 'origin province', page: 'Address' })"
            @input="clearAutoCompleteAddress('origin')"
          >
            <option
              v-for="province in provincesInThailand"
              :value="province.key"
              :key="province.key"
            >
              {{ province.th }}
            </option>
          </b-select>
          <b-input
            v-validate="'required'"
            v-else
            v-model="form.originProvince"
            :placeholder="$t('kyc.province_placeholder')"
            name="origin province"
            autocomplete="disabled"
            expanded
            data-test="kyc-address--origin-province-field"
            @blur="$emit('trackInputForm', { key: 'originProvince', name: 'origin province', page: 'Address' })"
          />
        </b-field>
        <span class="is-invalid">{{ errors.first('origin province') }}</span>
      </div>
    </div>
    <div class="columns dp-flex">
      <div class="column">
        <b-field>
          <div>
            <b-checkbox
              v-model="isCurrentAddressSameOrigin"
              data-test="kyc-address--current-address-same-origin-checkbox"
            >
              {{ $t('kyc.current_addr_same_origin') }}
            </b-checkbox>
          </div>
        </b-field>
      </div>
    </div>

    <!-- CURRENT ADDRESS  -->

    <div v-show="!isCurrentAddressSameOrigin">
      <div class="columns dp-flex">
        <div class="column">
          <b-field
            id="current-address"
            :label="$t('kyc.current_address')"
          >
            <b-input
              v-validate="'required'"
              v-model="form.currentAddressLineOne"
              :placeholder="$t('kyc.address_placeholder')"
              name="current address"
              autocomplete="disabled"
              expanded
              data-test="kyc-address--current-address-field"
              @blur="$emit('trackInputForm', { key: 'currentAddressLineOne', name: 'current address', page: 'Address' })"
            />
          </b-field>
          <span class="is-invalid">{{ errors.first('current address') }}</span>
        </div>
      </div>
      <div class="columns dp-flex">
        <div class="column">
          <b-field :label="$t('kyc.address_line_2')">
            <b-input
              v-model="form.currentAddressLineTwo"
              :placeholder="$t('kyc.address_placeholder')"
              name="current address line two"
              autocomplete="disabled"
              expanded
              data-test="kyc-address--current-address-line-2-field"
              @blur="$emit('trackInputForm', { key: 'currentAddressLineTwo', name: 'currentAddressLineTwo', page: 'Address' })"
            />
          </b-field>
        </div>
      </div>
      <div class="columns dp-flex">
        <div class="column">
          <b-field
            id="current-country"
            :label="$t('kyc.country')"
            class="kyc-country-is-expanded"
          >
            <b-select
              v-validate="'required'"
              v-model="form.currentCountry"
              :placeholder="$t('kyc.country')"
              name="current country"
              data-test="kyc-address--current-country-field"
              @blur="$emit('trackInputForm', { key: 'currentCountry', name: 'current country', page: 'Address' })"
              @input="() => {
                form.currentProvince = null
                $forceUpdate()
              }"
            >
              <option
                v-for="(country, index) in countries"
                :value="country.en_short_name"
                :key="index"
                :disabled="country.disabled"
              >
                {{ country.en_short_name }}
              </option>
            </b-select>
          </b-field>
          <span class="is-invalid">{{ errors.first('current country') }}</span>
        </div>
        <div class="column">
          <b-field
            id="current-postcode"
            :label="$t('kyc.zipcode')"
          >
            <ThailandAutoComplete
              v-validate="form.currentCountry === 'Thailand' ? 'required|numeric|length:5': 'required'"
              v-if="form.currentCountry === 'Thailand'"
              v-model="form.currentPostcode"
              :placeholder="$t('kyc.zipcode')"
              color="#337ab7"
              name="current postcode"
              type="zipcode"
              data-test="kyc-address--current-postcode-field"
              expanded
              @select="(address) => selectThailandAddrAutoComplete('current', address)"
              @blur="$emit('trackInputForm', { key: 'currentPostcode', name: 'current postcode', page: 'Address' })"
            />
            <b-input
              v-validate="form.currentCountry === 'Thailand' ? 'required|numeric|length:5': 'required'"
              v-else
              v-model="form.currentPostcode"
              :placeholder="$t('kyc.zipcode')"
              name="current postcode"
              autocomplete="disabled"
              expanded
              data-test="kyc-address--current-postcode-field"
              @blur="$emit('trackInputForm', { key: 'currentPostcode', name: 'current postcode', page: 'Address' })"
            />
          </b-field>
          <span class="is-invalid">{{ errors.first('current postcode') }}</span>
        </div>
      </div>
      <div class="columns dp-flex">
        <div class="column">
          <b-field
            id="current-city"
            :label="$t('kyc.city')"
          >
            <ThailandAutoComplete
              v-validate="'required'"
              v-if="form.currentCountry === 'Thailand'"
              v-model="form.currentCity"
              :placeholder="$t('kyc.city_placeholder')"
              color="#337ab7"
              name="current city"
              type="amphoe"
              data-test="kyc-address--current-city-field"
              expanded
              @select="(address) => selectThailandAddrAutoComplete('current', address)"
              @blur="$emit('trackInputForm', { key: 'currentCity', name: 'current city', page: 'Address' })"
            />
            <b-input
              v-validate="'required'"
              v-else
              v-model="form.currentCity"
              :placeholder="$t('kyc.city_placeholder')"
              name="current city"
              autocomplete="disabled"
              expanded
              data-test="kyc-address--current-city-field"
              @blur="$emit('trackInputForm', { key: 'currentCity', name: 'current city', page: 'Address' })"
            />
          </b-field>
          <span class="is-invalid">{{ errors.first('current city') }}</span>
        </div>
        <div class="column">
          <b-field
            id="current-province"
            :label="$t('kyc.province')"
          >
            <b-select
              v-validate="'required'"
              v-if="form.currentCountry === 'Thailand'"
              v-model="form.currentProvince"
              :placeholder="$t('kyc.province_placeholder')"
              name="current province"
              expanded
              data-test="kyc-address--current-province-field"
              @blur="$emit('trackInputForm', { key: 'currentProvince', name: 'current province', page: 'Address' })"
              @input="clearAutoCompleteAddress('current')"
            >
              <option
                v-for="province in provincesInThailand"
                :value="province.key"
                :key="province.key"
              >
                {{ province.th }}
              </option>
            </b-select>
            <b-input
              v-validate="'required'"
              v-else
              v-model="form.currentProvince"
              :placeholder="$t('kyc.province_placeholder')"
              name="current province"
              autocomplete="disabled"
              expanded
              data-test="kyc-address--current-province-field"
              @blur="$emit('trackInputForm', { key: 'currentProvince', name: 'current province', page: 'Address' })"
            />
          </b-field>
          <span class="is-invalid">{{ errors.first('current province') }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ThailandAutoComplete from 'vue-thailand-address-autocomplete'

export default {
  inject: ['$validator'],
  components: {
    ThailandAutoComplete
  },
  props: {
    form: {
      type: Object,
      default: () => {}
    },
    countries: {
      type: Array,
      default: () => []
    },
    provincesInThailand: {
      type: Array,
      default: () => []
    }
  },
  data () {
    return {
      isCurrentAddressSameOrigin: false
    }
  },
  computed: {
    originAddressLineOne () {
      return this.form.originAddressLineOne
    },
    originAddressLineTwo () {
      return this.form.originAddressLineTwo
    },
    originProvince () {
      return this.form.originProvince
    },
    originCity () {
      return this.form.originCity
    },
    originPostcode () {
      return this.form.originPostcode
    }
  },
  watch: {
    isCurrentAddressSameOrigin (value) {
      if (value) {
        this.form.currentAddressLineOne = this.form.originAddressLineOne
        this.form.currentAddressLineTwo = this.form.originAddressLineTwo
        this.form.currentProvince = this.form.originProvince
        this.form.currentCity = this.form.originCity
        this.form.currentPostcode = this.form.originPostcode
        this.form.currentCountry = this.form.originCountry
      } else {
        this.form.currentAddressLineOne = ''
        this.form.currentAddressLineTwo = ''
        this.form.currentProvince = ''
        this.form.currentCity = ''
        this.form.currentPostcode = ''
        this.form.currentCountry = ''
      }
    },
    originAddressLineOne (value) {
      if (this.isCurrentAddressSameOrigin) {
        this.form.currentAddressLineOne = value
      }
    },
    originAddressLineTwo (value) {
      if (this.isCurrentAddressSameOrigin) {
        this.form.currentAddressLineTwo = value
      }
    },
    originProvince (value) {
      if (this.isCurrentAddressSameOrigin) {
        this.form.currentProvince = value
      }
    },
    originCity (value) {
      if (this.isCurrentAddressSameOrigin) {
        this.form.currentCity = value
      }
    },
    originPostcode (value) {
      if (this.isCurrentAddressSameOrigin) {
        this.form.currentPostcode = value
      }
    }
  },
  async created () {
    if (this.form.nationality && !this.form.originCountry && !this.form.currentCountry) {
      const defaultCountry = this.countries.find(c => c.nationality === this.form.nationality)
      if (defaultCountry) {
        this.form.originCountry = defaultCountry['en_short_name']
        this.form.currentCountry = defaultCountry['en_short_name']
      }
    }
  },
  beforeDestroy () {
    this.$validator.pause()
  },
  methods: {
    selectThailandAddrAutoComplete (type, address) {
      if (type === 'origin') {
        this.form.originPostcode = address.zipcode
        this.form.originCity = address.amphoe
        this.form.originProvince = this.provincesInThailand.find(p => p.th === address.province).key
      } else if (type === 'current') {
        this.form.currentPostcode = address.zipcode
        this.form.currentCity = address.amphoe
        this.form.currentProvince = this.provincesInThailand.find(p => p.th === address.province).key
      }
    },
    clearAutoCompleteAddress (type) {
      if (type === 'origin') {
        this.form.originPostcode = ''
        this.form.originCity = ''
      } else if (type === 'current') {
        this.form.currentPostcode = ''
        this.form.currentCity = ''
      }
    }
  }
}
</script>

<style lang="scss">
.kyc-country-is-expanded {
  .control {
    .select {
      width: 100%;
      select {
        width: 100%;
      }
    }
  }
}
</style>

<style lang="scss" scoped>
.dp-flex {
  display: flex;
}
</style>



// WEBPACK FOOTER //
// src/pages/KYC/components/Address.vue